import { computed, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { UserWMSFilterModel, UserWMSListModel } from '@kanzi-apes/kanzi-models';
import {
  injectQuery,
  injectQueryClient,
  keepPreviousData,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { UserConfigWMSQueryKeys, UsersRolesWMSQueryKeys, UsersWMSQueryKeys } from './query-key.factory';
import { UsersWMSService } from '../services/users-wms/users-wms.service';

@Injectable({
  providedIn: 'root',
})
export class UsersWMSQueries {
  private kanziUsersWMSService = inject(UsersWMSService);
  private queryClient = injectQueryClient();
  private page = signal(1);
  private userWMSListFilters = signal<UserWMSFilterModel | null>(null);

  public userWMSListQuery = injectQuery(() => ({
    queryKey: UsersWMSQueryKeys.all(this.userWMSListFilters()),
    queryFn: () =>
      lastValueFrom(
        this.kanziUsersWMSService.getUserList(this.userWMSListFilters())
      ),
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
  }));

  public getUserConfigWMS = (filterSignal: Signal<UserWMSFilterModel | null>) =>
    injectQuery(() => ({
      queryKey: UserConfigWMSQueryKeys.all(filterSignal() as UserWMSFilterModel),
      queryFn: () =>
        lastValueFrom(
          this.kanziUsersWMSService.getUserConfig(filterSignal() as UserWMSFilterModel)
        ),
      retry: 2,
      select: (payload) => payload.results[0],
      enabled: filterSignal() !== null,
    }));

    public getUserConfigUrlWMS =  (filterSignal: Signal<UserWMSFilterModel | null>) =>
      injectQuery(() => ({
        queryKey: UserConfigWMSQueryKeys.getUrl(filterSignal() as UserWMSFilterModel),
        queryFn: () =>
          lastValueFrom(
            this.kanziUsersWMSService.getUrlPresignedToUploadPicture(filterSignal() as UserWMSFilterModel)
          ),
        retry: 2,           
        enabled: filterSignal()?.username !== null,
      }));


  public userRolesWMSListQuery = injectQuery(() => ({
    queryKey: UsersRolesWMSQueryKeys.all(),
    queryFn: () =>
      lastValueFrom(
        this.kanziUsersWMSService.getUserRolesList()
      ),
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
  }));

  public updateUsersWMSFilters(newFilters: UserWMSFilterModel) {
    this.userWMSListFilters.update((currentFilter) => ({
      ...currentFilter,
      ...newFilters,
    }));

    this.page.update((old) =>
      this.userWMSListQuery.data()?.last_evaluated_key ? old + 1 : old
    );
  }

  constructor() {
    effect(() => {
      if (
        !this.userWMSListQuery.isPlaceholderData() &&
        this.userWMSListQuery.data()?.last_evaluated_key
      ) {
        this.queryClient.prefetchQuery({
          queryKey: UsersWMSQueryKeys.all({
            ...this.userWMSListFilters(),
            last_evaluated_key_id:
              this.userWMSListQuery.data()?.last_evaluated_key,
          }),
          queryFn: () =>
            lastValueFrom(
              this.kanziUsersWMSService.getUserList({
                ...this.userWMSListFilters(),
                last_evaluated_key_id:
                  this.userWMSListQuery.data()?.last_evaluated_key,
              })
            ),
        });
      }
    });
  }
}
